<template>
    <div class="grid grid-cols-2 gap-2 lg:gap-4">
        <template
            v-for="(block, blockIdx) in value"
            :key="block.uuid"
        >
            <div
                v-if="!movingBlockUuid || canDropBlockAt(blockIdx)"
                class="col-span-2 flex justify-center"
            >
                <button
                    v-if="movingBlockUuid"
                    type="button"
                    class="w-full py-2 text-xs text-center transition border border-dashed rounded-md text-myflowPurple border-myflowPurple bg-myflowPurple-50 hover:bg-myflowPurple-100"
                    @click.prevent="dropMovingBlockAt(blockIdx)"
                >
                    {{ _mft('pagebuilder:block.moveHere') }}
                </button>
                <button
                    v-else
                    type="button"
                    class="w-full py-2 text-xs text-center text-gray-500 transition border border-dashed rounded-md border-gray-300 hover:border-myflowPurple hover:text-myflowPurple"
                    @click.prevent="openBlockPanel(blockIdx)"
                >
                    <span class="inline-flex items-center space-x-1">
                        <i class="fa-regular fa-plus"></i>
                        <span>{{ _mft('blockEditor:addBlockHere') }}</span>
                    </span>
                </button>
            </div>
            <div
                v-if="movingBlockUuid && !canDropBlockAt(blockIdx)"
                class="py-2 border-t border-transparent"
            >&nbsp;</div>
            <card-basic
                :id="`block-${block.uuid}`"
                :class="[
                    { half: 'col-span-2 lg:col-span-1', full: 'col-span-2'}[block.layout],
                    'col-span-2',
                    isMovingBlock(block) ? 'ring-2 ring-myflowPurple ring-offset-2' : '',
                    wasRecentlyMoved(block) ? 'block-editor__block--moved' : '',
                ]"
            >
                <div class="lg:flex justify-between items-center" :class="isMovingBlock(block) ? '' : 'pb-2 mb-3 border-b'">
                    <div class="items-center mb-2 md:flex lg:mb-0">
                        <h3 class="pl-px md:pl-0"><i>Block {{blockIdx + 1}}:</i> {{namesForBlocks[blockIdx]}}</h3>
                        <div class="md:ml-4" v-if="showVisibilityTools">
                            <slot name="block-header-buttons" v-bind:block="block">
                                <div class="flex space-x-1 rounded-full">
                                    <button-base
                                        @click.prevent="block.hidden = false"
                                        size="sm"
                                        :style-type="!block.hidden ? 'tinted' : 'secondary'"
                                    >
                                        {{ _mft('shared:show') }}
                                    </button-base>

                                    <button-base
                                        @click.prevent="block.hidden = true"
                                        size="sm"
                                        :style-type="block.hidden ? 'tinted' : 'secondary'"
                                    >
                                        {{ _mft('shared:hide') }}
                                    </button-base>
                                </div>
                            </slot>
                        </div>
                    </div>
                    <div class="flex items-center justify-between">
                        <div>

                        </div>
                        <div class="flex items-center justify-end space-x-2 align-center">
                            <block-editor-layout-selector
                                v-if="blockTypesForBlocks[blockIdx]"
                                class="pr-3 mr-1 border-r"
                                :block="block"
                                :available-layouts="blockTypesForBlocks[blockIdx].availableLayouts"
                            ></block-editor-layout-selector>

                            <button-danger
                                v-if="! protectedBlockTypes.includes(block.type) && !isMovingBlock(block)"
                                @click.prevent="removeBlock(blockIdx)"
                            ><i class="fa-regular fa-trash"></i></button-danger>
                            <button-base
                                :style-type="isMovingBlock(block) ? 'tinted' : 'secondary'"
                                @click.prevent="toggleMovingBlock(block)"
                            >{{ isMovingBlock(block) ? _mft('pagebuilder:block.cancelMove') : _mft('pagebuilder:block.move') }}</button-base>
                        </div>
                    </div>
                </div>

                <div :class="isMovingBlock(block) ? 'max-h-0 overflow-hidden' : ''">
                    <component
                        class="transition-opacity duration-300 ease-in-out"
                        :class="[block.hidden ? 'opacity-50 hover:opacity-100' : '']"
                        :key="block.uuid"
                        :is="`blocks-${block.type}`"
                        :layout="block.layout"
                        v-model="block.content"
                    ></component>

                    <BlockConditionalDisplaySettings
                        v-if="showVisibilityTools"
                        :block="block"
                        :block-index="blockIdx"
                        :blocks="value"
                        :conditions="block.conditions ? block.conditions : []"
                        @updateConditions="block.conditions = $event"
                    />
                </div>
            </card-basic>
        </template>
        <div
            v-if="value.length > 0 && (!movingBlockUuid || canDropBlockAt(value.length))"
            class="col-span-2 flex justify-center"
        >
            <button
                v-if="movingBlockUuid"
                type="button"
                class="w-full py-2 text-xs text-center transition border border-dashed rounded-md text-myflowPurple border-myflowPurple bg-myflowPurple-50 hover:bg-myflowPurple-100"
                @click.prevent="dropMovingBlockAt(value.length)"
            >
                {{ _mft('pagebuilder:block.moveHere') }}
            </button>
            <button
                v-else
                type="button"
                class="w-full py-2 text-xs text-center text-gray-500 transition border border-dashed rounded-md border-gray-300 hover:border-myflowPurple hover:text-myflowPurple"
                @click.prevent="openBlockPanel(value.length)"
            >
                <span class="inline-flex items-center space-x-1">
                    <i class="fa-regular fa-plus"></i>
                    <span>{{ _mft('blockEditor:addBlockHere') }}</span>
                </span>
            </button>
        </div>
    </div>

    <div
        class="absolute inset-0"
        v-if="blockpanelOpen"
        v-on:click="closeBlockPanel()"
    ></div>
    <Teleport
        :disabled="value.length === 0"
        v-if="loaded"
        to="#teleport-target-block-editor-toolbar"
    >
        <div class="items-center justify-center ">
            <div
                class="relative flex flex-col items-center justify-center"
            >

                <div v-if="$slots['after-add-blocks-buttons']" class="mb-4">
                    <slot name="after-add-blocks-buttons"></slot>
                </div>
                <button-base
                    style-type="primary"
                    @click.prevent="toggleBlockPanel()"
                >
                    <template #icon>
                        <icon-plus class="w-4 h-4 -ml-1" />
                    </template>
                    <span class="ml-1">{{ _mft('blockEditor:addBlock') }}</span>
                </button-base>
            </div>
        </div>
    </Teleport>
    <slideout-with-slots
        v-if="blockpanelOpen"
        @close="closeBlockPanel()"
    >
        <template #body>
            <div>
                <div v-for="(section, idx) in groupsWithBlockTypes" v-bind:key="idx">
                    <div class="-mx-4 lg:-mx-6 pl-4 lg:pl-6 border-b mb-4 flex">
                        <span class="inline-block font-bold -mb-2.5 px-2 bg-white">{{ section.group.heading }}</span>
                    </div>
                    <div class="grid grid-cols-3 gap-px mb-2">
                        <button
                            v-for="(blockType, indexBlockType) in section.blockTypes"
                            :key="indexBlockType"
                            type="button"
                            class="m-2 min-h-[4rem] transition-all bg-white rounded-xl border relative flex items-start justify-center text-xs group hover:border-wlPrimary hover:text-gray-600"
                            @click.prevent="addBlock(blockType.type)"
                        >
                            <div
                                class="flex flex-col items-center justify-center w-full h-full p-2 100 rounded-xl"
                                :class="disabledBlockTypes.includes(blockType.type) ? 'opacity-25 cursor-not-allowed' : ''"
                            >
                                <h5>
                                    <i v-for="icon in blockType.icons" v-bind:key="icon" class="p-1 fa-regular" :class="icon"></i>
                                </h5>
                                <div v-html="blockType.name"></div>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </template>
    </slideout-with-slots>
</template>
<style lang="scss">
button:disabled.myflow-basic-button {
    opacity: 0.5;
    pointer-events: none;
}

.block-editor__block--moved {
    animation: block-editor-moved 900ms ease-out;
    transform-origin: center top;
}

@keyframes block-editor-moved {
    0% {
        transform: translateY(-4px) scale(0.985);
        box-shadow: 0 0 0 0 rgba(90, 58, 150, 0.18);
    }

    35% {
        transform: translateY(0) scale(0.985);
        box-shadow: 0 0 0 4px rgba(90, 58, 150, 0.12);
    }

    55% {
        transform: translateY(0) scale(0.985);
        box-shadow: 0 0 0 4px rgba(90, 58, 150, 0.08);
    }

    100% {
        transform: translateY(0) scale(1);
        box-shadow: 0 0 0 0 rgba(90, 58, 150, 0);
    }
}

</style>
<script>
import { v4 as uuidv4 } from 'uuid';

import BlocksHeadline           from './blocks/edit/headline.vue';
import BlocksText               from './blocks/edit/text.vue';
import BlocksVideo              from './blocks/edit/video.vue';
import BlocksVideoWithNotes     from './blocks/edit/videoWithNotes.vue';
import BlocksImage              from './blocks/edit/image.vue';
import BlocksFile               from './blocks/edit/file.vue';
import BlocksCardText           from './blocks/edit/cardText.vue';
import BlocksCardTextList       from './blocks/edit/cardTextList.vue';
import BlocksHeader             from './blocks/edit/header.vue';
import BlocksImageDescription   from './blocks/edit/imageDescription.vue';
import BlocksImageText          from './blocks/edit/imageText.vue';
import BlocksFullWidthImage     from './blocks/edit/fullWidthImage.vue';
import BlocksNavigationButtons  from './blocks/edit/navigationButtons.vue';
import BlocksQuote              from './blocks/edit/quote.vue';
import BlocksSlideshow          from './blocks/edit/slideshow.vue';
import BlocksSlimProduct        from './blocks/edit/slimProduct.vue';
import BlocksTextWithTitle      from './blocks/edit/textWithTitle.vue';
import BlocksLoomEmbed          from './blocks/edit/loomEmbed.vue';
import BlocksVideoRecorder      from './blocks/edit/videoRecorder.vue';
import BlocksLeadCapture        from './blocks/edit/leadCapture.vue';
import BlocksSocialMedia        from './blocks/edit/socialMedia.vue';
import BlocksVotingSuggestions  from './blocks/edit/votingSuggestions.vue';
import BlocksLivestream         from './blocks/edit/livestream.vue';
import BlocksToc                from './blocks/edit/tableOfContents.vue';
import BlocksCalendly           from './blocks/edit/calendly.vue';
import BlocksAiIntroduction     from './blocks/edit/aiIntroduction.vue';
import BlocksPricingOffer       from './blocks/edit/pricingOffer.vue';
import BlocksImageFromUrl       from './blocks/edit/imageFromUrl.vue';
import BlocksFaq                from './blocks/edit/faq.vue';
import BlocksAudio              from './blocks/edit/audio.vue';
import BlocksVimeo              from './blocks/edit/vimeo.vue';
import BlocksVimeoLive          from './blocks/edit/vimeo-live.vue';
import BlocksForm               from './blocks/edit/form.vue';
import BlocksFormsInputText     from './blocks/edit/forms-input/_basic.vue';
import BlocksFormsInputNumber   from './blocks/edit/forms-input/_basic.vue';
import BlocksFormsInputScore    from './blocks/edit/forms-input/_score.vue';
import BlocksFormsInputEmail    from './blocks/edit/forms-input/_basic.vue';
import BlocksFormsInputPhone    from './blocks/edit/forms-input/_basic.vue';
import BlocksFormsInputDate     from './blocks/edit/forms-input/_basic.vue';
import BlocksFormsInputTime     from './blocks/edit/forms-input/_basic.vue';
import BlocksFormsInputDateTime from './blocks/edit/forms-input/_basic.vue';
import BlocksFormsInputTextarea from './blocks/edit/forms-input/_basic.vue';
import BlocksFormsInputRadio    from './blocks/edit/forms-input/_multi.vue';
import BlocksFormsInputCheckbox from './blocks/edit/forms-input/_multi.vue';
import BlocksFormsInputSelect   from './blocks/edit/forms-input/_multi.vue';
import BlocksFormsInputUpload   from './blocks/edit/forms-input/_basic.vue';
import BlocksFormsInputCrmContactName   from './blocks/edit/forms-input/_crmContactRequired.vue';
import BlocksFormsInputCrmContactEmail   from './blocks/edit/forms-input/_crmContactRequired.vue';
import BlocksFormsInputCrmContactTitle   from './blocks/edit/forms-input/_crmContactOptional.vue';
import BlocksFormsInputCrmContactPhone   from './blocks/edit/forms-input/_crmContactOptional.vue';
import BlocksFormsInputCrmContactCompanyName   from './blocks/edit/forms-input/_crmContactOptional.vue';
import BlocksFormsInputCrmContactCompanyBillingEmail   from './blocks/edit/forms-input/_crmContactOptional.vue';
import BlocksFormsInputCrmContactCompanyWebsite   from './blocks/edit/forms-input/_crmContactOptional.vue';
import BlocksFormsLayoutPageBreak   from './blocks/edit/forms-layout/_pageBreak.vue';
import BlocksLogo               from './blocks/edit/logo.vue';
import BlocksCronofy            from './blocks/edit/cronofy.vue';
import BlocksEmailHeader        from './blocks/edit/email/header.vue';
import BlocksEmailPanel         from './blocks/edit/email/panel.vue';
import BlocksEmailButton        from './blocks/edit/email/button.vue';
import BlocksEmailText          from './blocks/edit/email/text.vue';
import BlocksEmailSubcopy       from './blocks/edit/email/subcopy.vue';
import BlocksEmailImage         from './blocks/edit/email/image.vue';
import BlocksEmailImageWithLink from './blocks/edit/email/imageWithLink.vue';
import BlocksEmailSocialMedia   from './blocks/edit/email/socialMedia.vue';
import IconPlus from "../icons/iconPlus.vue";
import BlockConditionalDisplaySettings from "./blockConditionalDisplaySettings.vue";

const BLOCK_TYPES = [
    {
        type: 'headline',
        group: 'content',
        name: _mft('blocks:headline'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-heading'
        ]
    },
    {
        type: 'header',
        group: 'content',
        name: _mft('blocks:header'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-heading'
        ]
    },
    {
        type: 'text-with-title',
        group: 'content',
        name: _mft('blocks:text-with-title'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-heading',
            'fa-align-left'
        ]
    },
    {
        type: 'text',
        group: 'content',
        name: _mft('blocks:text'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-align-left'
        ]
    },
    {
        type: 'video',
        group: 'media',
        name: _mft('blocks:video'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-video'
        ]
    },
    {
        type: 'video-with-notes',
        group: 'media',
        name: _mft('blocks:video-with-notes'),
        availableByDefault: false,
        availableLayouts: ['full'],
        icons: [
            'fa-video'
        ]
    },
    {
        type: 'image',
        group: 'media',
        name: _mft('blocks:image'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-image'
        ]
    },
    {
        type: 'image-from-url',
        group: 'media',
        name: _mft('blocks:image-from-url'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-image',
            'fa-link'
        ]
    },
    {
        type: 'full-width-image',
        group: 'media',
        name: _mft('blocks:full-width-image'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-image-landscape'
        ]
    },
    {
        type: 'logo',
        group: 'media',
        name: _mft('blocks:logo'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-hexagon-image'
        ]
    },
    {
        type: 'audio',
        group: 'media',
        name: _mft('blocks:audio'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-volume'
        ]
    },
    {
        type: 'file',
        group: 'media',
        name: _mft('blocks:file'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-download'
        ]
    },
    {
        type: 'vimeo',
        group: 'integrations',
        name: _mft('blocks:vimeo'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-brands fa-vimeo'
        ]
    },
    {
        type: 'vimeo-live',
        group: 'integrations',
        name: _mft('blocks:vimeo-live'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-brands fa-vimeo'
        ]
    },
    {
        type: 'card-text',
        group: 'content',
        name: _mft('blocks:card-text'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-square'
        ]
    },
    {
        type: 'card-text-list',
        group: 'content',
        name: _mft('blocks:card-text-list'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-rectangle-list'
        ]
    },
    {
        type: 'image-description',
        group: 'media',
        name: _mft('blocks:image-description'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-message-image'
        ]
    },
    {
        type: 'quote',
        group: 'content',
        name: _mft('blocks:quote'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-quote-right'
        ]
    },
    {
        type: 'faq',
        group: 'content',
        name: _mft('blocks:faq'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-question',
            'fa-maximize'
        ]
    },
    {
        type: 'form',
        group: 'actions',
        name: _mft('blocks:form'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-input-pipe'
        ]
    },
    {
        type: 'slideshow',
        group: 'media',
        name: _mft('blocks:slideshow'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-presentation-screen'
        ]
    },
    {
        type: 'slim-product',
        group: 'media',
        name: _mft('blocks:slim-product'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-cart-shopping'
        ]
    },
    {
        type: 'pricing-offer',
        group: 'media',
        name: _mft('blocks:pricing-offer'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-cart-plus'
        ]
    },
    {
        type: 'loom-embed',
        group: 'integrations',
        name: _mft('blocks:loom-embed'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-camera-web'
        ]
    },
    {
        type: 'video-recorder',
        group: 'media',
        name: _mft('blocks:video-recorder'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-camera-web'
        ]
    },
    {
        type: 'lead-capture',
        group: 'actions',
        name: _mft('blocks:lead-capture'),
        availableByDefault: false,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-address-card'
        ]
    },
    {
        type: 'social-media',
        group: 'actions',
        name: _mft('blocks:social-media'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-share-nodes'
        ]
    },
    {
        type: 'voting-suggestions',
        group: 'actions',
        name: _mft('blocks:voting-suggestions'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-check-to-slot'
        ]
    },
    {
        type: 'livestream',
        group: 'media',
        name: _mft('blocks:livestream'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-signal-stream'
        ]
    },
    {
        type: 'calendly',
        group: 'integrations',
        name: _mft('blocks:calendly'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-calendar-days'
        ]
    },
    {
        type: 'cronofy',
        group: 'integrations',
        name: _mft('blocks:cronofy'),
        availableByDefault: false,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-calendar-days'
        ]
    },
    {
        type: 'ai-introduction',
        group: 'integrations',
        name: _mft('blocks:ai-introduction'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-handshake'
        ]
    },
    {
        type: 'table-of-contents',
        group: 'actions',
        name: _mft('blocks:table-of-contents'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-check-to-slot'
        ]
    },

    // FORMS

    {
        type: 'forms-input-text',
        group: 'forms-input',
        name: _mft('blocks:forms-input-text'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-input-text'
        ]
    },
    {
        type: 'forms-input-number',
        group: 'forms-input',
        name: _mft('blocks:forms-input-number'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-input-numeric'
        ]
    },
    {
        type: 'forms-input-score',
        group: 'forms-input',
        name: _mft('blocks:forms-input-score'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-star'
        ]
    },
    {
        type: 'forms-input-email',
        group: 'forms-input',
        name: _mft('blocks:forms-input-email'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-at'
        ]
    },
    {
        type: 'forms-input-phone',
        group: 'forms-input',
        name: _mft('blocks:forms-input-phone'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-phone'
        ]
    },
    {
        type: 'forms-input-date',
        group: 'forms-input',
        name: _mft('blocks:forms-input-date'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-calendar'
        ]
    },
    {
        type: 'forms-input-time',
        group: 'forms-input',
        name: _mft('blocks:forms-input-time'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-clock'
        ]
    },
    {
        type: 'forms-input-date-time',
        group: 'forms-input',
        name: _mft('blocks:forms-input-date-time'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-calendar-clock'
        ]
    },
    {
        type: 'forms-input-textarea',
        group: 'forms-input',
        name: _mft('blocks:forms-input-textarea'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-line-columns'
        ]
    },
    {
        type: 'forms-input-radio',
        group: 'forms-input',
        name: _mft('blocks:forms-input-radio'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-list-radio'
        ]
    },
    {
        type: 'forms-input-checkbox',
        group: 'forms-input',
        name: _mft('blocks:forms-input-checkbox'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-list-check'
        ]
    },
    {
        type: 'forms-input-select',
        group: 'forms-input',
        name: _mft('blocks:forms-input-select'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-list-dropdown'
        ]
    },
    {
        type: 'forms-input-upload',
        group: 'forms-input',
        name: _mft('blocks:forms-input-upload'),
        availableByDefault: true,
        availableLayouts: ['half', 'full'],
        icons: [
            'fa-upload'
        ]
    },
    {
        type: 'forms-page-break',
        group: 'forms-layout',
        name: _mft('blocks:forms-page-break'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-file-dashed-line'
        ]
    },
    {
        type: 'forms-input-crm-contact-name',
        group: 'forms-crm',
        name: _mft('blocks:forms-input-crm-contact-name'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-address-card'
        ]
    },
    {
        type: 'forms-input-crm-contact-email',
        group: 'forms-crm',
        name: _mft('blocks:forms-input-crm-contact-email'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-address-card'
        ]
    },
    {
        type: 'forms-input-crm-contact-title',
        group: 'forms-crm',
        name: _mft('blocks:forms-input-crm-contact-title'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-address-card'
        ]
    },
    {
        type: 'forms-input-crm-contact-phone',
        group: 'forms-crm',
        name: _mft('blocks:forms-input-crm-contact-phone'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-address-card'
        ]
    },
    {
        type: 'forms-input-crm-contact-company-name',
        group: 'forms-crm',
        name: _mft('blocks:forms-input-crm-contact-company-name'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-address-card'
        ]
    },
    {
        type: 'forms-input-crm-contact-company-billing-email',
        group: 'forms-crm',
        name: _mft('blocks:forms-input-crm-contact-company-billing-email'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-address-card'
        ]
    },
    {
        type: 'forms-input-crm-contact-company-website',
        group: 'forms-crm',
        name: _mft('blocks:forms-input-crm-contact-company-website'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-address-card'
        ]
    },

    // EMAIL

    {
        type: 'email-header',
        group: 'email',
        name: _mft('blockEditor:block.heading.name'),
        availableByDefault: false,
        availableLayouts: ['full'],
        icons: [
            'fa-heading'
        ]
    },
    {
        type: 'email-text',
        group: 'email',
        name: _mft('blockEditor:block.content.name'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-align-left'
        ]
    },
    {
        type: 'email-panel',
        group: 'email',
        name: _mft('blockEditor:block.panel.name'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-rectangle-list'
        ]
    },
    {
        type: 'email-button',
        group: 'email',
        name: _mft('blockEditor:block.button.name'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-square-up-right'
        ]
    },
    {
        type: 'email-subcopy',
        group: 'email',
        name: _mft('blockEditor:block.footer.name'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-file-lines'
        ]
    },
    {
        type: 'email-image',
        group: 'email',
        name: _mft('blockEditor:block.image.name'),
        availableByDefault: false, // Replaced by email-image-with-link
        availableLayouts: ['full'],
        icons: [
            'fa-image'
        ]
    },
    {
        type: 'email-image-with-link',
        group: 'email',
        name: _mft('blockEditor:block.image.name'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-image'
        ]
    },
    {
        type: 'email-social-media',
        group: 'email',
        name: _mft('blockEditor:block.socialMedia.name'),
        availableByDefault: true,
        availableLayouts: ['full'],
        icons: [
            'fa-share-nodes'
        ]
    },
];
const GROUPS = [
    {
        id: 'forms-input',
        heading: 'Formulärfält',
        availableByDefault: false
    },
    {
        id: 'forms-layout',
        heading: 'Layout',
        availableByDefault: false
    },
    {
        id: 'forms-crm',
        heading: 'CRM',
        availableByDefault: false
    },
    {
        id: 'content',
        heading: 'Innehåll',
        availableByDefault: true
    },
    {
        id: 'media',
        heading: 'Media',
        availableByDefault: true
    },
    {
        id: 'actions',
        heading: 'Actions',
        availableByDefault: true
    },
    {
        id: 'integrations',
        heading: 'Integrationer',
        availableByDefault: true
    },
    {
        id: 'email',
        heading: _mft('blockEditor:group.email.name'),
        availableByDefault: false
    },
];
export default {
    components: {
        BlockConditionalDisplaySettings,
        IconPlus,
        BlocksAiIntroduction,
        BlocksAudio,
        BlocksCalendly,
        BlocksCardText,
        BlocksCardTextList,
        BlocksCronofy,
        BlocksFaq,
        BlocksFile,
        BlocksForm,
        BlocksFormsInputCheckbox,
        BlocksFormsInputDate,
        BlocksFormsInputDateTime,
        BlocksFormsInputEmail,
        BlocksFormsInputNumber,
        BlocksFormsInputScore,
        BlocksFormsInputPhone,
        BlocksFormsInputRadio,
        BlocksFormsInputSelect,
        BlocksFormsInputText,
        BlocksFormsInputTextarea,
        BlocksFormsInputTime,
        BlocksFormsInputUpload,
        BlocksFormsInputCrmContactName,
        BlocksFormsInputCrmContactEmail,
        BlocksFormsInputCrmContactTitle,
        BlocksFormsInputCrmContactPhone,
        BlocksFormsInputCrmContactCompanyName,
        BlocksFormsInputCrmContactCompanyBillingEmail,
        BlocksFormsInputCrmContactCompanyWebsite,
        BlocksFormsLayoutPageBreak,
        BlocksHeader,
        BlocksHeadline,
        BlocksImage,
        BlocksImageDescription,
        BlocksImageFromUrl,
        BlocksImageText,
        BlocksFullWidthImage,
        BlocksLeadCapture,
        BlocksLivestream,
        BlocksLogo,
        BlocksLoomEmbed,
        BlocksNavigationButtons,
        BlocksPricingOffer,
        BlocksQuote,
        BlocksSlideshow,
        BlocksSlimProduct,
        BlocksSocialMedia,
        BlocksText,
        BlocksTextWithTitle,
        BlocksToc,
        BlocksVideo,
        BlocksVideoRecorder,
        BlocksVideoWithNotes,
        BlocksVimeo,
        BlocksVimeoLive,
        BlocksVotingSuggestions,
        BlocksEmailHeader,
        BlocksEmailPanel,
        BlocksEmailButton,
        BlocksEmailText,
        BlocksEmailSubcopy,
        BlocksEmailImage,
        BlocksEmailImageWithLink,
        BlocksEmailSocialMedia,
    },
    props: {
        modelValue: Array,
        blockTypes: { type: Array, default: null },
        blockGroups: { type: Array, default: null },
        includedBlockTypes: { type: Array, default: [] },
        excludedBlockTypes: { type: Array, default: [] },
        disabledBlockTypes: { type: Array, default: [] },
        protectedBlockTypes: { type: Array, default: [] },
        includedBlockGroups: { type: Array, default: [] },
        excludedBlockGroups: { type: Array, default: [] },
        showVisibilityTools: { type: Boolean, default: true },
    },
    emits: [
        'update:modelValue'
    ],
    data() {
        return {
            value: [],
            blockpanelOpen: false,
            blockPanelInsertIndex: null,
            blockCount: 0,
            loaded: false,
            movingBlockUuid: null,
            recentlyMovedBlockUuid: null,
            recentlyMovedBlockTimeout: null,
            hideToggleClass: 'px-4 first:rounded-l-lg lg:first:rounded-l-full lg:first:pl-4 last:rounded-r-lg lg:last:rounded-r-full lg:last:pr-5 py-1 lg:py-1 border-r last:border-r-0 cursor-pointer text-xs'
        };
    },
    computed: {
        namesForBlocks() {
            return this.value.map(block => {
                const bt = BLOCK_TYPES.find((bt) => bt.type == block.type);
                return bt ? bt.name : block.type;
            });
        },
        blockTypesForBlocks() {
            return this.value.map(block => BLOCK_TYPES.find((bt) => bt.type == block.type));
        },
        movingBlockIndex() {
            if (!this.movingBlockUuid) {
                return -1;
            }

            return this.value.findIndex(block => block.uuid === this.movingBlockUuid);
        },
        groupsWithBlockTypes() {
            const availableBlockTypes = BLOCK_TYPES
                .filter((bt) => {
                    if (this.blockTypes !== null) {
                        return (this.blockTypes.indexOf(bt.type) !== -1);
                    } else {
                        return (
                            (bt.availableByDefault || this.includedBlockTypes.indexOf(bt.type) !== -1)
                            &&
                            this.excludedBlockTypes.indexOf(bt.type) === -1
                        );
                    }
                });

            return GROUPS
                .filter((group) => {
                    if (this.blockGroups !== null) {
                        return (this.blockGroups.indexOf(group.id) !== -1);
                    } else {
                        return (
                            (group.availableByDefault || this.includedBlockGroups.indexOf(group.id) !== -1)
                            &&
                            this.excludedBlockGroups.indexOf(group.id) === -1
                        );
                    }
                })
                .map((group) => ({
                    group: group,
                    blockTypes: availableBlockTypes.filter((bt) => bt.group == group.id)
                }))
                .filter((section) => section.blockTypes.length > 0);
        }
    },
    mounted() {
        this.captureModelValue();
        let self = this;
        setTimeout(function(){
            self.loaded = true;
        }, 1);
    },
    beforeUnmount() {
        clearTimeout(this.recentlyMovedBlockTimeout);
    },
    watch: {
        modelValue() {
            if (JSON.stringify(this.value) === JSON.stringify(this.modelValue)) { return; }
            this.captureModelValue();
        },
        movingBlockIndex(idx) {
            if (this.movingBlockUuid && idx === -1) {
                this.movingBlockUuid = null;
            }
        },
        value: {
            handler() {
                this.$emit('update:modelValue', JSON.parse(JSON.stringify(this.value)));
                let self = this;
                setTimeout(function(){
                    self.blockCount = self.value.length;
                }, 250); // Update blockcount one animation for blockpanel has ended
            },
            deep: true
        }
    },
    methods: {
        captureModelValue() {
            this.value = this.modelValue ? JSON.parse(JSON.stringify(this.modelValue)) : [];
            this.value.forEach(block => {
                if (!block.uuid) {
                    block.uuid = uuidv4();
                }
            });
        },
        openBlockPanel(insertIndex = null) {
            this.movingBlockUuid = null;
            this.blockPanelInsertIndex = insertIndex;
            this.blockpanelOpen = true;
        },
        closeBlockPanel() {
            this.blockpanelOpen = false;
            this.blockPanelInsertIndex = null;
        },
        toggleBlockPanel() {
            this.movingBlockUuid = null;

            if (this.blockpanelOpen) {
                this.closeBlockPanel();
                return;
            }

            this.openBlockPanel();
        },
        normalizedInsertIndex() {
            if (this.blockPanelInsertIndex === null) {
                return this.value.length;
            }

            return Math.max(0, Math.min(this.blockPanelInsertIndex, this.value.length));
        },
        addBlock(type) {
            if (this.disabledBlockTypes.includes(type)) {
                return;
            }

            const block = {
                type: type,
                uuid: uuidv4(),
                layout: 'full',
                content: {}
            };

            this.value.splice(this.normalizedInsertIndex(), 0, block);
            this.closeBlockPanel();
            this.scrollToBlock(block.uuid);
        },
        importBlock(block, insertIndex = null) {
            const blockToImport = JSON.parse(JSON.stringify(block));

            if (!blockToImport.uuid) {
                blockToImport.uuid = uuidv4();
            }

            if (insertIndex === null) {
                this.value.push(blockToImport);
                return;
            }

            this.value.splice(Math.max(0, Math.min(insertIndex, this.value.length)), 0, blockToImport);
        },
        scrollToLastBlock() {
            const lastBlock = this.value[this.value.length - 1];

            if (lastBlock) {
                this.scrollToBlock(lastBlock.uuid);
            }
        },
        scrollToBlock(uuid) {
            setTimeout(function(){
                const element = document.getElementById(`block-${uuid}`);

                if (element) {
                    element.scrollIntoView({behavior: "smooth", block: "center"});
                }
            }, 1); // scroll once animation has ended
        },
        removeBlock(idx) {
            const blockToDelete = this.value[idx];
            const isUsedInConditions = this.value.some(block => {
                return block.conditions?.or?.some(condition => condition.inputUuid === blockToDelete.uuid);
            });

            if (isUsedInConditions) {
                alert(_mft('form:field.conditionalDisplay.cannotBeDeleted'));
                return;
            }

            if( confirm(_mft('shared:confirm.cannotBeUndone')) ){
                this.value.splice(idx, 1);
            }
        },
        isMovingBlock(block) {
            return this.movingBlockUuid === block.uuid;
        },
        wasRecentlyMoved(block) {
            return this.recentlyMovedBlockUuid === block.uuid;
        },
        toggleMovingBlock(block) {
            this.blockpanelOpen = false;
            this.blockPanelInsertIndex = null;
            this.movingBlockUuid = this.isMovingBlock(block) ? null : block.uuid;
        },
        canDropBlockAt(insertIndex) {
            if (this.movingBlockIndex === -1) {
                return false;
            }

            return insertIndex !== this.movingBlockIndex && insertIndex !== this.movingBlockIndex + 1;
        },
        dropMovingBlockAt(insertIndex) {
            if (!this.canDropBlockAt(insertIndex)) {
                return;
            }

            const blockUuid = this.movingBlockUuid;
            this.moveBlockToIndex(this.movingBlockIndex, insertIndex);
            this.movingBlockUuid = null;
            this.markBlockAsRecentlyMoved(blockUuid);
            this.scrollToBlock(blockUuid);
        },
        markBlockAsRecentlyMoved(uuid) {
            clearTimeout(this.recentlyMovedBlockTimeout);
            this.recentlyMovedBlockUuid = null;

            this.$nextTick(() => {
                this.recentlyMovedBlockUuid = uuid;
                this.recentlyMovedBlockTimeout = setTimeout(() => {
                    this.recentlyMovedBlockUuid = null;
                }, 900);
            });
        },
        moveBlockToIndex(idx, insertIndex) {
            if (idx < 0 || idx >= this.value.length) {
                return;
            }

            const normalizedInsertIndex = Math.max(0, Math.min(insertIndex, this.value.length));
            const block = this.value.splice(idx, 1);
            const targetIndex = normalizedInsertIndex > idx ? normalizedInsertIndex - 1 : normalizedInsertIndex;

            this.value.splice(targetIndex, 0, JSON.parse(JSON.stringify(block[0])));
        },
    }
}
</script>
